import { Animal } from "../types/interfaces";
import { PregnancyRecord, UbicacionRecord, WeightRecord } from "./historicalArrays";

export interface CaravanaChainAnimal
  extends Pick<
    Animal,
    | "caravana"
    | "caravanas_viejas"
    | "pesajes"
    | "prenies_historial"
    | "ubicaciones_historial"
    | "peso"
    | "peso_inicial"
    | "fecha_de_evento_peso_inicial"
    | "fecha_modificacion_peso_inicial"
    | "prenies"
    | "tiene_ternero"
    | "fecha_nacimiento"
    | "fecha_de_creacion"
    | "estado"
    | "categoria"
    | "sexo"
    | "raza"
    | "edad"
    | "ubicacion_id"
    | "observaciones"
    | "empresa_id"
    | "renspa"
    | "es_comprado"
  > {}

type MergeableAnimal = {
  caravana: string;
  caravanas_viejas?: string[];
  pesajes?: WeightRecord[];
  prenies_historial?: PregnancyRecord[];
  ubicaciones_historial?: UbicacionRecord[];
  peso?: number;
  peso_inicial?: number;
  fecha_de_evento_peso_inicial?: string;
  fecha_modificacion_peso_inicial?: string;
  prenies?: string;
  tiene_ternero?: boolean;
  fecha_nacimiento?: string;
  estado?: string;
  [key: string]: any;
};

function deduplicatePesajes(pesajes: WeightRecord[]): WeightRecord[] {
  const seen = new Set<string>();
  return pesajes.filter((p) => {
    const key = `${p.fecha_evento}|${p.peso}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function deduplicatePrenies(
  prenies: PregnancyRecord[]
): PregnancyRecord[] {
  const seen = new Set<string>();
  return prenies.filter((p) => {
    const key = `${p.fecha_evento}|${p.prenies}|${p.tiene_ternero}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function deduplicateUbicaciones(
  ubicaciones: UbicacionRecord[]
): UbicacionRecord[] {
  const seen = new Set<string>();
  return ubicaciones.filter((u) => {
    const key = `${u.fecha_evento}|${u.ubicacion_id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Recursively collect all animals in the caravana chain on-demand.
 * Uses getAnimal function to fetch animals only when needed.
 */
async function collectChainOnDemand<T extends MergeableAnimal>(
  caravanasViejas: string[],
  getAnimal: (caravana: string) => Promise<T | undefined>,
  visited: Set<string>
): Promise<T[]> {
  const chainAnimals: T[] = [];

  for (const caravanaVieja of caravanasViejas) {
    if (visited.has(caravanaVieja)) continue;
    visited.add(caravanaVieja);

    const oldAnimal = await getAnimal(caravanaVieja);
    if (!oldAnimal) continue;

    chainAnimals.push(oldAnimal);

    if (oldAnimal.caravanas_viejas && oldAnimal.caravanas_viejas.length > 0) {
      const nested = await collectChainOnDemand(
        oldAnimal.caravanas_viejas,
        getAnimal,
        visited
      );
      chainAnimals.push(...nested);
    }
  }

  return chainAnimals;
}

/**
 * Campos de estado actual que se heredan de la cadena cuando el registro
 * propio no los tiene (KAN-175: recaravaneo no traspasaba ubicación ni sexo).
 *
 * Semántica: el registro propio NUNCA se pisa — propio > predecesor directo >
 * resto de la cadena (nearest-wins). Dentro de la cadena gana el primer
 * ancestro que tenga el campo (collectChainOnDemand devuelve al predecesor
 * directo primero).
 *
 * NO se heredan `estado` ni datos de venta (`info_venta`,
 * `operacion_venta_id`): el ciclo de vida del animal viejo (venta/baja)
 * pertenece a la caravana reemplazada y no debe marcarse en el animal activo.
 * Sí se hereda el lado compra: es el mismo animal físico.
 *
 * `lastEventField` es el timestamp `fecha_ultimo_evento_<campo>` pareado con
 * el valor: si el valor se hereda, su fecha de evento viaja con él para que
 * los análisis temporales sigan consistentes.
 */
export const CURRENT_STATE_FIELDS: Array<{ field: string; lastEventField?: string }> = [
  { field: 'sexo', lastEventField: 'fecha_ultimo_evento_sexo' },
  { field: 'ubicacion_id', lastEventField: 'fecha_ultimo_evento_ubicacion_id' },
  { field: 'categoria', lastEventField: 'fecha_ultimo_evento_categoria' },
  { field: 'raza', lastEventField: 'fecha_ultimo_evento_raza' },
  { field: 'edad', lastEventField: 'fecha_ultimo_evento_edad' },
  { field: 'observaciones', lastEventField: 'fecha_ultimo_evento_observaciones' },
  { field: 'prenies', lastEventField: 'fecha_ultimo_evento_prenies' },
  { field: 'tiene_ternero', lastEventField: 'fecha_ultimo_evento_tiene_ternero' },
  { field: 'renspa', lastEventField: 'fecha_ultimo_evento_renspa' },
  { field: 'es_comprado', lastEventField: 'fecha_ultimo_evento_es_comprado' },
  { field: 'info_compra' },
  { field: 'operacion_compra_id' },
  // Parentesco: campos simples (sin lastEventField). Al recaravaneo, el animal
  // nuevo hereda madre/padre de la cadena — el linaje no cambia con la caravana.
  { field: 'madre' },
  { field: 'padre' },
];

/**
 * Un valor "vacío" para herencia: undefined, null o string vacío.
 * OJO: `false` NO es vacío — un tiene_ternero/es_comprado=false es un dato
 * válido del animal, no un hueco (no usar truthiness acá).
 */
function isEmptyForInheritance(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

function performMerge<T extends MergeableAnimal>(
  animal: T,
  chainAnimals: T[]
): T {
  if (chainAnimals.length === 0) return animal;

  const merged = { ...animal };

  const allPesajes: WeightRecord[] = [
    ...(animal.pesajes || []),
  ];
  const allPrenies: PregnancyRecord[] = [
    ...(animal.prenies_historial || []),
  ];
  const allUbicaciones: UbicacionRecord[] = [
    ...(animal.ubicaciones_historial || []),
  ];

  // Collect all peso_inicial and fecha_de_evento_peso_inicial from the chain
  // to find the OLDEST (earliest) one - this represents the true first weighing
  const allPesoInicial: Array<{
    peso: number;
    fecha_evento?: string;
    fecha_modificacion?: string;
  }> = [];

  if (animal.peso_inicial !== undefined && animal.peso_inicial !== null) {
    allPesoInicial.push({
      peso: animal.peso_inicial,
      fecha_evento: animal.fecha_de_evento_peso_inicial,
      fecha_modificacion: animal.fecha_modificacion_peso_inicial,
    });
  }

  // Collect all fecha_nacimiento to find the oldest
  const allFechaNacimiento: string[] = [];
  if (animal.fecha_nacimiento) {
    allFechaNacimiento.push(animal.fecha_nacimiento);
  }

  // Collect all fecha_de_creacion to find the oldest (when animal was first created)
  const allFechaCreacion: string[] = [];
  if (animal.fecha_de_creacion) {
    allFechaCreacion.push(animal.fecha_de_creacion);
  }

  for (const chainAnimal of chainAnimals) {
    allPesajes.push(...(chainAnimal.pesajes || []));
    allPrenies.push(...(chainAnimal.prenies_historial || []));
    allUbicaciones.push(...(chainAnimal.ubicaciones_historial || []));

    // Collect peso_inicial from chain
    if (chainAnimal.peso_inicial !== undefined && chainAnimal.peso_inicial !== null) {
      allPesoInicial.push({
        peso: chainAnimal.peso_inicial,
        fecha_evento: chainAnimal.fecha_de_evento_peso_inicial,
        fecha_modificacion: chainAnimal.fecha_modificacion_peso_inicial,
      });
    }

    // Collect fecha_nacimiento from chain
    if (chainAnimal.fecha_nacimiento) {
      allFechaNacimiento.push(chainAnimal.fecha_nacimiento);
    }

    // Collect fecha_de_creacion from chain
    if (chainAnimal.fecha_de_creacion) {
      allFechaCreacion.push(chainAnimal.fecha_de_creacion);
    }

    // Inherit peso from chain if current animal doesn't have one
    if (
      (merged.peso === undefined || merged.peso === 0) &&
      chainAnimal.peso !== undefined &&
      chainAnimal.peso > 0
    ) {
      merged.peso = chainAnimal.peso;
    }
  }

  // Find the OLDEST peso_inicial (earliest fecha_de_evento_peso_inicial)
  if (allPesoInicial.length > 0) {
    // Sort by fecha_evento ascending (oldest first)
    allPesoInicial.sort((a, b) => {
      if (!a.fecha_evento) return 1;
      if (!b.fecha_evento) return -1;
      return new Date(a.fecha_evento).getTime() - new Date(b.fecha_evento).getTime();
    });
    const oldest = allPesoInicial[0];
    merged.peso_inicial = oldest.peso;
    merged.fecha_de_evento_peso_inicial = oldest.fecha_evento;
    merged.fecha_modificacion_peso_inicial = oldest.fecha_modificacion;
  }

  // Find the OLDEST fecha_nacimiento
  if (allFechaNacimiento.length > 0) {
    allFechaNacimiento.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    merged.fecha_nacimiento = allFechaNacimiento[0];
  }

  // Find the OLDEST fecha_de_creacion (when this animal was first created in the system)
  if (allFechaCreacion.length > 0) {
    allFechaCreacion.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    (merged as any).fecha_de_creacion = allFechaCreacion[0];
  }

  allPesajes.sort(
    (a, b) =>
      new Date(a.fecha_evento).getTime() -
      new Date(b.fecha_evento).getTime()
  );
  allPrenies.sort(
    (a, b) =>
      new Date(a.fecha_evento).getTime() -
      new Date(b.fecha_evento).getTime()
  );
  allUbicaciones.sort(
    (a, b) =>
      new Date(a.fecha_evento).getTime() -
      new Date(b.fecha_evento).getTime()
  );

  merged.pesajes = deduplicatePesajes(allPesajes);
  merged.prenies_historial = deduplicatePrenies(allPrenies);
  merged.ubicaciones_historial = deduplicateUbicaciones(allUbicaciones);

  // KAN-175: backfill de campos de estado actual desde la cadena.
  // Solo se llenan huecos del registro propio; nearest-wins dentro de la cadena.
  for (const { field, lastEventField } of CURRENT_STATE_FIELDS) {
    if (!isEmptyForInheritance((merged as any)[field])) continue;

    const donor = chainAnimals.find(
      (ca) => !isEmptyForInheritance((ca as any)[field])
    );
    if (!donor) continue;

    (merged as any)[field] = (donor as any)[field];

    if (
      lastEventField &&
      isEmptyForInheritance((merged as any)[lastEventField]) &&
      !isEmptyForInheritance((donor as any)[lastEventField])
    ) {
      (merged as any)[lastEventField] = (donor as any)[lastEventField];
    }
  }

  return merged;
}

/**
 * Merge a single animal with its caravana chain (on-demand fetching).
 * Fetches animals from the chain only as needed using getAnimal function.
 *
 * @param animal - The animal to merge
 * @param getAnimal - Async function to fetch a single animal by caravana
 * @returns The merged animal with data from its entire caravana chain
 */
export async function mergeSingleAnimal<T extends MergeableAnimal>(
  animal: T,
  getAnimal: (caravana: string) => Promise<T | undefined>
): Promise<T> {
  if (
    !animal.caravanas_viejas ||
    animal.caravanas_viejas.length === 0
  ) {
    return animal;
  }

  const visited = new Set<string>([animal.caravana]);
  const chainAnimals = await collectChainOnDemand(
    animal.caravanas_viejas,
    getAnimal,
    visited
  );

  return performMerge(animal, chainAnimals);
}

/**
 * Merge multiple animals with their caravana chains (on-demand fetching).
 * Fetches animals from the chain only as needed using getAnimal function.
 *
 * @param animals - Array of animals to merge
 * @param getAnimal - Async function to fetch a single animal by caravana
 * @returns Array of merged animals, excluding RECARAVANEADO animals that were merged
 */
export async function mergeCaravanaChain<T extends MergeableAnimal>(
  animals: T[],
  getAnimal: (caravana: string) => Promise<T | undefined>
): Promise<T[]> {
  const result: T[] = [];
  const mergedCaravanas = new Set<string>();

  for (const animal of animals) {
    if (
      !animal.caravanas_viejas ||
      animal.caravanas_viejas.length === 0
    ) {
      result.push(animal);
      continue;
    }

    const visited = new Set<string>([animal.caravana]);
    const chainAnimals = await collectChainOnDemand(
      animal.caravanas_viejas,
      getAnimal,
      visited
    );

    if (chainAnimals.length === 0) {
      result.push(animal);
      continue;
    }

    // Mark RECARAVANEADO animals as merged (they will be excluded from result)
    chainAnimals.forEach((ca) => {
      if (ca.estado === 'RECARAVANEADO') {
        mergedCaravanas.add(ca.caravana);
      }
    });

    const merged = await performMerge(animal, chainAnimals);
    result.push(merged);
  }

  return result.filter((a) => !mergedCaravanas.has(a.caravana));
}

/**
 * Returns the full chain of caravanas (including the target) following
 * caravanas_viejas recursively using on-demand fetching.
 *
 * @param animal - The animal to get the chain for
 * @param getAnimal - Async function to fetch a single animal by caravana
 * @returns Array of all caravanas in the chain
 */
export async function getAllCaravanasInChain<T extends MergeableAnimal>(
  animal: T,
  getAnimal: (caravana: string) => Promise<T | undefined>
): Promise<string[]> {
  const result = new Set<string>([animal.caravana]);
  const visited = new Set<string>([animal.caravana]);

  async function collectCaravanas(caravanasViejas: string[]) {
    for (const caravanaVieja of caravanasViejas) {
      if (visited.has(caravanaVieja)) continue;
      visited.add(caravanaVieja);

      const oldAnimal = await getAnimal(caravanaVieja);
      if (!oldAnimal) continue;

      result.add(caravanaVieja);

      if (oldAnimal.caravanas_viejas && oldAnimal.caravanas_viejas.length > 0) {
        await collectCaravanas(oldAnimal.caravanas_viejas);
      }
    }
  }

  if (animal.caravanas_viejas && animal.caravanas_viejas.length > 0) {
    await collectCaravanas(animal.caravanas_viejas);
  }

  return Array.from(result);
}
